// v2/pages/admin/AdminOrdersV2.js — Yonetim / Siparisler (/v2/admin/orders)
// URL: ?q= &code= &group= &state= &warehouse= &from= &to= &all= &sort= &dir= &page= &per=
// Siparis degistirme YOK. Silme: yalnizca detaydan, onizleme + onay ile (v2_siparis_sil).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ShoppingBag, Search, X, SlidersHorizontal, RotateCw, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, ChevronsUpDown, Calendar, CalendarDays, Store, AlertTriangle,
  Inbox, Loader2, Clock, Receipt, Boxes, Info, Trash2,
} from 'lucide-react';
import useAdminOrders, { siparisKalemleri } from '../../hooks/useAdminOrders';
import { formatTRY, formatTRYShort, formatNumber, formatPercent } from '../../utils/format';
import { zamanTR, gunUzun, DEPO_ETIKET, SIPARIS_DURUM } from '../../utils/adminFormat';
import { SkelPanel } from '../../components/Skeleton';
import ProductThumb from '../../components/ProductThumb';
import OrderDeleteModal from '../../components/admin/OrderDeleteModal';
import { YENI_SIPARIS_OLAYI } from '../../components/admin/SiparisBildirimi';
import ToastStack from '../../components/Toast';
import useToast from '../../hooks/useToast';
import s from '../../styles/AdminOrdersV2.module.css';
import '../../styles/tokens.css';

const DURUMLAR = [
  { id: '', label: 'Tüm durumlar' },
  { id: 'transferred', label: 'Aktarıldı' },
  { id: 'processing', label: 'İşlemde' },
  { id: 'error', label: 'Hata' },
];
const DEPOLAR = [
  { id: '', label: 'Tüm depolar' },
  { id: 'gzt', label: 'Gaziantep' },
  { id: 'ist', label: 'İstanbul' },
];
const GRUPLAR = ['', 'Dizel', 'Elektrik', 'CARGO', 'Direksiyon', 'Klima'];
const KOLONLAR = [
  { id: 'time', label: 'Tarih / Saat', sort: 'time' },
  { id: 'cust', label: 'Müşteri' },
  { id: 'lines', label: 'Kalem', sort: 'lines', num: true },
  { id: 'qty', label: 'Adet', sort: 'qty', num: true },
  { id: 'amount', label: 'Tutar', sort: 'amount', num: true },
  { id: 'wh', label: 'Depo' },
  { id: 'erp', label: 'ERP Fiş' },
  { id: 'state', label: 'Durum' },
];
const VARSAYILAN = { sort: 'time', dir: 'desc', page: '1', per: '20' };
const HIZLI_TARIH = [
  { id: 'today', label: 'Bugün' },
  { id: 'yesterday', label: 'Dün' },
  { id: '7', label: 'Son 7 gün' },
  { id: '30', label: 'Son 30 gün' },
  { id: 'month', label: 'Bu ay' },
];

const tarihTR = (ymd) => {
  if (!ymd) return '';
  const [y, a, g] = String(ymd).split('-');
  return y ? `${g}.${a}.${y}` : '';
};

/** Date -> "2026-09-17" (yerel) */
const ymdKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Sunucu "2026-09-17T18:54:00" verir; saat dilimi cevrimi yapmadan parcala */
const gunuAl = (iso) => (iso ? String(iso).slice(0, 10) : '');
const saatTR = (iso) => (iso ? String(iso).slice(11, 16) : '-');

function araligiCoz(id) {
  const bugun = new Date();
  if (id === 'today') return { from: ymdKey(bugun), to: ymdKey(bugun) };
  if (id === 'yesterday') {
    const d = new Date(bugun);
    d.setDate(d.getDate() - 1);
    return { from: ymdKey(d), to: ymdKey(d) };
  }
  if (id === 'month') {
    return { from: ymdKey(new Date(bugun.getFullYear(), bugun.getMonth(), 1)), to: ymdKey(bugun) };
  }
  const b = new Date(bugun);
  b.setDate(b.getDate() - (Number(id) - 1));
  return { from: ymdKey(b), to: ymdKey(bugun) };
}

function gunBasligi(g) {
  const bugun = new Date();
  const dun = new Date();
  dun.setDate(dun.getDate() - 1);
  if (g === ymdKey(bugun)) return 'Bugün';
  if (g === ymdKey(dun)) return 'Dün';
  return gunUzun(g) || tarihTR(g);
}

/** Satir acilinca kalemleri getirir */
function Kalemler({ nolar }) {
  const [veri, setVeri] = useState(null);
  const [hata, setHata] = useState(null);

  useEffect(() => {
    let iptal = false;
    siparisKalemleri(nolar)
      .then((v) => {
        if (iptal) return;
        if (!v || !v.success) throw new Error((v && v.message) || 'Kalemler alınamadı');
        setVeri(v.items || []);
      })
      .catch((e) => { if (!iptal) setHata((e && e.message) || 'Kalemler alınamadı'); });
    return () => { iptal = true; };
  }, [nolar]);

  if (hata) return <div className={s.lineErr}><AlertTriangle size={15} /> {hata}</div>;
  if (!veri) return <div className={s.lineLoad}><Loader2 size={15} className={s.spin} /> Kalemler yükleniyor…</div>;
  if (!veri.length) return <div className={s.lineErr}><Inbox size={15} /> Kalem bulunamadı</div>;

  const topl = veri.reduce((a, k) => ({
    adet: a.adet + (k.qty_gzt || 0) + (k.qty_ist || 0),
    tutar: a.tutar + (Number(k.total) || 0),
  }), { adet: 0, tutar: 0 });

  return (
    <div className={s.lines}>
      <div className={s.lineSum}>
        <span><b>{formatNumber(veri.length)}</b> kalem</span>
        <span><b>{formatNumber(topl.adet)}</b> adet</span>
        <span>Toplam <b>{formatTRY(topl.tutar)}</b></span>
      </div>
      <div className={s.lineWrap}>
        <table className={s.lineTable}>
          <thead>
            <tr>
              <th className={s.lth}>Ürün Kodu</th>
              <th className={s.lth}>Ürün Adı</th>
              <th className={s.lth}>Grup</th>
              <th className={`${s.lth} ${s.num}`}>Brüt</th>
              <th className={`${s.lth} ${s.num}`}>İnd.</th>
              <th className={`${s.lth} ${s.num}`}>Net</th>
              <th className={`${s.lth} ${s.num}`}>GZT</th>
              <th className={`${s.lth} ${s.num}`}>İST</th>
              <th className={`${s.lth} ${s.num}`}>Toplam</th>
            </tr>
          </thead>
          <tbody>
            {veri.map((k, i) => (
              <tr key={`${k.order_no}-${k.product_code}-${i}`}>
                <td className={s.ltd}>
                  <span className={s.pcell}>
                    <ProductThumb
                      code={k.product_code}
                      name={k.product_name || k.product_code}
                      className={s.thumb}
                      imgClassName={s.thumbImg}
                    />
                    <span className={s.mono}>{k.product_code}</span>
                  </span>
                </td>
                <td className={s.ltd}><span className={s.pname} title={k.product_name || ''}>{k.product_name || '-'}</span></td>
                <td className={s.ltd}><span className={s.gchip}>{k.group || '-'}</span></td>
                <td className={`${s.ltd} ${s.num} ${s.dim}`}>{formatTRY(k.gross)}</td>
                <td className={`${s.ltd} ${s.num} ${s.disc}`}>{k.discount ? formatPercent(k.discount) : '-'}</td>
                <td className={`${s.ltd} ${s.num}`}>{formatTRY(k.net)}</td>
                <td className={`${s.ltd} ${s.num}`}>{k.qty_gzt || '-'}</td>
                <td className={`${s.ltd} ${s.num}`}>{k.qty_ist || '-'}</td>
                <td className={`${s.ltd} ${s.num} ${s.strong}`}>{formatTRY(k.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className={s.lineList}>
        {veri.map((k, i) => (
          <li key={`ml-${k.order_no}-${k.product_code}-${i}`} className={s.lineItem}>
            <div className={s.lineTop}>
              <ProductThumb
                code={k.product_code}
                name={k.product_name || k.product_code}
                className={s.thumb}
                imgClassName={s.thumbImg}
              />
              <span className={s.pname}>{k.product_name || k.product_code}</span>
              <span className={s.strong}>{formatTRY(k.total)}</span>
            </div>
            <div className={s.lineMeta}>
              <span className={s.mono}>{k.product_code}</span>
              <span>{formatTRY(k.net)} × {(k.qty_gzt || 0) + (k.qty_ist || 0)}</span>
              {k.discount ? <span className={s.disc}>İnd. {formatPercent(k.discount)}</span> : null}
              <span>{k.qty_gzt ? `GZT ${k.qty_gzt}` : ''}{k.qty_gzt && k.qty_ist ? ' · ' : ''}{k.qty_ist ? `İST ${k.qty_ist}` : ''}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Acik siparis detayinin altindaki silme butonu */
function SilButonu({ onClick }) {
  return (
    <div className={s.expActions}>
      <button type="button" className={s.delOrderBtn} onClick={onClick}>
        <Trash2 size={14} /> Siparişi Sil
      </button>
    </div>
  );
}

export default function AdminOrdersV2() {
  const [sp, setSp] = useSearchParams();
  const oku = useCallback((k) => sp.get(k) || VARSAYILAN[k] || '', [sp]);
  const [arama, setArama] = useState(sp.get('q') || '');
  const [filtreAcik, setFiltreAcik] = useState(false);
  const [acikSatir, setAcikSatir] = useState(null);
  const [silinecek, setSilinecek] = useState(null);
  const toast = useToast();

  useEffect(() => { setArama(sp.get('q') || ''); }, [sp]);

  const params = useMemo(() => ({
    q: sp.get('q') || '', code: sp.get('code') || '', group: sp.get('group') || '',
    state: sp.get('state') || '', warehouse: sp.get('warehouse') || '',
    from: sp.get('from') || '', to: sp.get('to') || '', all: sp.get('all') || '',
    sort: oku('sort'), dir: oku('dir'), page: oku('page'), per: oku('per'),
  }), [sp, oku]);

  const { data, loading, error, yenile } = useAdminOrders(params);

  // yeni siparis bildirimi gelince liste kendiliginden yenilensin
  useEffect(() => {
    const dinle = () => yenile();
    window.addEventListener(YENI_SIPARIS_OLAYI, dinle);
    return () => window.removeEventListener(YENI_SIPARIS_OLAYI, dinle);
  }, [yenile]);

  const silKapat = useCallback(() => setSilinecek(null), []);
  const silindi = useCallback((mesaj) => {
    setSilinecek(null);
    setAcikSatir(null);
    toast.push(mesaj || 'Sipariş silindi');
    yenile();
  }, [toast, yenile]);

  const guncelle = useCallback((yeni, sayfaSifirla = true) => {
    setSp((onceki) => {
      const p = new URLSearchParams(onceki);
      Object.entries(yeni).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined || v === VARSAYILAN[k]) p.delete(k);
        else p.set(k, v);
      });
      if (sayfaSifirla) p.delete('page');
      return p;
    }, { replace: true });
    setAcikSatir(null);
  }, [setSp]);

  useEffect(() => {
    const suAn = sp.get('q') || '';
    if (arama === suAn) return undefined;
    const t = setTimeout(() => guncelle({ q: arama.trim() }), 400);
    return () => clearTimeout(t);
  }, [arama, sp, guncelle]);

  const sirala = (alan) => {
    if (!alan) return;
    const ayni = params.sort === alan;
    guncelle({ sort: alan, dir: ayni && params.dir === 'desc' ? 'asc' : 'desc' });
  };

  const ilk = loading && !data;
  const ogeler = (data && data.items) || [];
  const gunOzet = useMemo(() => {
    const m = {};
    ogeler.forEach((o) => {
      const g = gunuAl(o.time);
      if (!m[g]) m[g] = { n: 0, tutar: 0 };
      m[g].n += 1;
      m[g].tutar += Number(o.amount) || 0;
    });
    return m;
  }, [ogeler]);
  const ozet = (data && data.summary) || {};
  const pencere = (data && data.window) || {};
  const sayfa = (data && data.page) || 1;
  const sonSayfa = (data && data.total_pages) || 1;
  const toplam = (data && data.total) || 0;
  const per = Number(params.per) || 20;
  const ilkSira = (sayfa - 1) * per;
  const acikFiltre = ['code', 'group', 'state', 'warehouse', 'from', 'to', 'all']
    .filter((k) => params[k]).length;

  const chipler = [];
  if (params.code) chipler.push({ id: 'code', ikon: Store, label: 'Müşteri', value: params.code, clear: { code: '' } });
  if (params.state) chipler.push({ id: 'state', label: 'Durum', value: (DURUMLAR.find((x) => x.id === params.state) || {}).label, clear: { state: '' } });
  if (params.warehouse) chipler.push({ id: 'warehouse', label: 'Depo', value: (DEPOLAR.find((x) => x.id === params.warehouse) || {}).label, clear: { warehouse: '' } });
  if (params.group) chipler.push({ id: 'group', label: 'Grup', value: params.group, clear: { group: '' } });
  if (params.from || params.to) chipler.push({ id: 'date', ikon: CalendarDays, label: 'Tarih', value: `${tarihTR(params.from) || '…'} – ${tarihTR(params.to) || 'bugün'}`, clear: { from: '', to: '' } });
  if (params.all === '1') chipler.push({ id: 'all', ikon: Clock, label: 'Dönem', value: 'Tüm zamanlar', clear: { all: '' } });

  const SiraIkon = ({ alan }) => {
    if (!alan) return null;
    if (params.sort !== alan) return <ChevronsUpDown size={13} className={s.sortIdle} />;
    return params.dir === 'asc'
      ? <ChevronUp size={13} className={s.sortOn} />
      : <ChevronDown size={13} className={s.sortOn} />;
  };

  return (
    <div className={s.page}>
      <div className={s.head}>
        <div className={s.headText}>
          <h2 className={s.title}>Siparişler</h2>
          <p className={s.subtitle}>
            {ilk ? 'Yükleniyor…' : (
              <>
                <b>{formatNumber(toplam)}</b> sipariş
                {pencere.custom
                  ? <> · {tarihTR(pencere.from) || 'başlangıç'} – {tarihTR(pencere.to) || 'bugün'}</>
                  : pencere.default_days
                    ? <> · son {pencere.default_days} gün</>
                    : <> · tüm zamanlar</>}
                {' '}· KDV hariç
              </>
            )}
          </p>
        </div>
        <button type="button" className={s.ghostBtn} onClick={yenile} disabled={loading}>
          <RotateCw size={15} className={loading ? s.spin : ''} />
          <span className={s.btnText}>Yenile</span>
        </button>
      </div>

      <div className={s.stats}>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tBlue}`}><ShoppingBag size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(ozet.orders)}</span>
            <span className={s.statLabel}>Sipariş · {formatNumber(ozet.customers)} müşteri</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tGreen}`}><Receipt size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatTRYShort(ozet.amount)}</span>
            <span className={s.statLabel}>Toplam tutar</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tViolet}`}><Boxes size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(ozet.qty)}</span>
            <span className={s.statLabel}>Adet · {formatNumber(ozet.lines)} kalem</span>
          </span>
        </div>
        <button
          type="button"
          className={`${s.stat} ${s.statBtn} ${params.state === 'error' ? s.statOn : ''}`}
          onClick={() => guncelle({ state: params.state === 'error' ? '' : 'error' })}
          title="Aktarım hatası alan siparişleri göster"
        >
          <span className={`${s.statIcon} ${ozet.errors ? s.tRed : s.tSlate}`}><AlertTriangle size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(ozet.errors)}</span>
            <span className={s.statLabel}>Aktarım hatası</span>
          </span>
        </button>
      </div>

      <div className={s.toolbar}>
        <div className={s.searchBox}>
          <Search size={16} className={s.searchIcon} />
          <input
            type="search" className={s.search}
            placeholder="Müşteri, sipariş no, ürün kodu veya adı…"
            value={arama} onChange={(e) => setArama(e.target.value)}
            aria-label="Sipariş ara"
          />
          {arama && (
            <button type="button" className={s.clearBtn} onClick={() => setArama('')} aria-label="Temizle">
              <X size={15} />
            </button>
          )}
        </div>
        <button
          type="button" className={`${s.ghostBtn} ${filtreAcik ? s.ghostOn : ''}`}
          onClick={() => setFiltreAcik((v) => !v)} aria-expanded={filtreAcik}
        >
          <SlidersHorizontal size={15} />
          <span className={s.btnText}>Filtreler</span>
          {acikFiltre > 0 && <span className={s.badge}>{acikFiltre}</span>}
        </button>
      </div>

      {filtreAcik && (
        <div className={s.filters}>
          <div className={s.presets}>
            <span className={s.fieldLabel}><CalendarDays size={12} /> Hızlı tarih</span>
            <div className={s.presetBtns}>
              {HIZLI_TARIH.map((h) => {
                const a = araligiCoz(h.id);
                const secili = params.from === a.from && params.to === a.to;
                return (
                  <button
                    key={h.id} type="button"
                    className={`${s.preset} ${secili ? s.presetOn : ''}`}
                    onClick={() => guncelle(secili ? { from: '', to: '' } : { ...a, all: '' })}
                  >{h.label}</button>
                );
              })}
            </div>
          </div>
          <label className={s.field}>
            <span className={s.fieldLabel}><Calendar size={12} /> Başlangıç</span>
            <input type="date" className={s.input} value={params.from}
              onChange={(e) => guncelle({ from: e.target.value, all: '' })} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}><Calendar size={12} /> Bitiş</span>
            <input type="date" className={s.input} value={params.to}
              onChange={(e) => guncelle({ to: e.target.value, all: '' })} />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Durum</span>
            <select className={s.select} value={params.state}
              onChange={(e) => guncelle({ state: e.target.value })}>
              {DURUMLAR.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Depo</span>
            <select className={s.select} value={params.warehouse}
              onChange={(e) => guncelle({ warehouse: e.target.value })}>
              {DEPOLAR.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>Ürün Grubu</span>
            <select className={s.select} value={params.group}
              onChange={(e) => guncelle({ group: e.target.value })}>
              {GRUPLAR.map((g) => <option key={g || 'all'} value={g}>{g || 'Tüm gruplar'}</option>)}
            </select>
          </label>
          <div className={s.filterActions}>
            <button
              type="button"
              className={`${s.toggle} ${params.all === '1' ? s.toggleOn : ''}`}
              onClick={() => guncelle({ all: params.all === '1' ? '' : '1', from: '', to: '' })}
              title="Tüm geçmişi tara (daha yavaş)"
            >
              <Clock size={14} /> Tüm zamanlar
            </button>
            {(acikFiltre > 0 || params.q) && (
              <button type="button" className={s.resetBtn} onClick={() => setSp({}, { replace: true })}>
                <X size={14} /> Temizle
              </button>
            )}
          </div>
        </div>
      )}

      {chipler.length > 0 && (
        <div className={s.chipRow}>
          {chipler.map((c) => {
            const Ikon = c.ikon;
            return (
              <span key={c.id} className={s.filterChip}>
                {Ikon ? <Ikon size={13} /> : null}
                {c.label}: <b>{c.value}</b>
                <button type="button" onClick={() => guncelle(c.clear)} aria-label="Filtreyi kaldır"><X size={13} /></button>
              </span>
            );
          })}
          <button type="button" className={s.chipClear} onClick={() => setSp({}, { replace: true })}>
            Tümünü temizle
          </button>
        </div>
      )}

      {error && (
        <div className={s.alert}>
          <AlertTriangle size={18} /> <span>{error}</span>
          <button type="button" className={s.alertBtn} onClick={yenile}>Tekrar dene</button>
        </div>
      )}

      <section className={s.panel}>
        {loading && !ilk && <span className={s.loadBar} aria-hidden="true" />}
        {ilk ? (
          <div className={s.panelBody}><SkelPanel rows={6} head={false} /></div>
        ) : ogeler.length === 0 ? (
          <div className={s.empty}>
            <Inbox size={24} />
            <p className={s.emptyTitle}>Sipariş bulunamadı</p>
            <p className={s.emptyText}>
              {pencere.default_days
                ? `Son ${pencere.default_days} gün tarandı. Daha eskisi için tarih seçin veya "Tüm zamanlar"ı açın.`
                : 'Arama veya filtreleri değiştirmeyi deneyin.'}
            </p>
          </div>
        ) : (
          <>
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={`${s.th} ${s.thNo}`}>#</th>
                    {KOLONLAR.map((k) => (
                      <th
                        key={k.id}
                        className={`${s.th} ${k.num ? s.num : ''} ${k.sort ? s.thSort : ''}`}
                        onClick={() => sirala(k.sort)}
                      >
                        <span className={s.thInner}>{k.label}<SiraIkon alan={k.sort} /></span>
                      </th>
                    ))}
                    <th className={`${s.th} ${s.thExp}`} aria-label="Kalemler" />
                  </tr>
                </thead>
                <tbody>
                  {ogeler.map((o, i) => {
                    const anahtar = `${o.code}-${o.time}`;
                    const acik = acikSatir === anahtar;
                    const d = SIPARIS_DURUM[o.state] || SIPARIS_DURUM.processing;
                    const gun = gunuAl(o.time);
                    const yeniGun = params.sort === 'time' && gun && gun !== gunuAl(i > 0 ? ogeler[i - 1].time : '');
                    const go = gunOzet[gun] || { n: 0, tutar: 0 };
                    return (
                      <React.Fragment key={anahtar}>
                        {yeniGun && (
                          <tr className={s.dayRow}>
                            <td className={s.dayTd} colSpan={10}>
                              <span className={s.dayLabel}>{gunBasligi(gun)}</span>
                              <span className={s.dayCount}>
                                {formatNumber(go.n)} sipariş · {formatTRY(go.tutar)}
                              </span>
                            </td>
                          </tr>
                        )}
                        <tr
                          className={`${s.row} ${acik ? s.rowOn : ''}`}
                          onClick={() => setAcikSatir(acik ? null : anahtar)}
                        >
                          <td className={`${s.td} ${s.tdNo}`}>{ilkSira + i + 1}</td>
                          <td className={s.td}>
                            <span className={s.cell}>
                              <span className={s.timeMain}>{saatTR(o.time)}</span>
                              <span className={s.sub}>
                                {o.order_no}{o.order_nos.length > 1 ? ` +${o.order_nos.length - 1}` : ''}
                              </span>
                            </span>
                          </td>
                          <td className={s.td}>
                            <span className={s.cell}>
                              <Link
                                to={`/v2/admin/customers/${encodeURIComponent(o.code)}`}
                                className={s.custLink} title={o.title || ''}
                                onClick={(e) => e.stopPropagation()}
                              >{o.title || o.code}</Link>
                              <span className={s.sub}>{o.code}</span>
                            </span>
                          </td>
                          <td className={`${s.td} ${s.num}`}>{formatNumber(o.lines)}</td>
                          <td className={`${s.td} ${s.num}`}>{formatNumber(o.qty)}</td>
                          <td className={`${s.td} ${s.num} ${s.strong}`}>{formatTRY(o.amount)}</td>
                          <td className={`${s.td} ${s.wh}`}>{DEPO_ETIKET[o.warehouse] || o.warehouse}</td>
                          <td className={`${s.td} ${s.mono} ${s.dim}`}>{o.erp_no || '-'}</td>
                          <td className={s.td}>
                            <span className={`${s.status} ${s[d.cls]}`} title={o.error_message || ''}>{d.label}</span>
                          </td>
                          <td className={`${s.td} ${s.tdExp}`}>
                            {acik ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                          </td>
                        </tr>
                        {acik && (
                          <tr className={s.expRow}>
                            <td className={s.expTd} colSpan={10}>
                              <div className={s.expHead}>
                                <span className={s.expMeta}><Clock size={13} /> {zamanTR(o.time)}</span>
                                <span className={s.expMeta}><Receipt size={13} /> {o.order_nos.join(', ')}</span>
                                {o.erp_no && <span className={s.expMeta}><Boxes size={13} /> {o.erp_no}</span>}
                                {o.groups && <span className={s.expMeta}>{o.groups}</span>}
                              </div>
                              {o.error_message && (
                                <div className={s.errNote}><AlertTriangle size={15} /> <b>Aktarım hatası:</b> {o.error_message}</div>
                              )}
                              {o.note && (
                                <div className={s.note}><Info size={15} /> <b>Açıklama:</b> {o.note}</div>
                              )}
                              <Kalemler nolar={o.order_nos} />
                              <SilButonu onClick={() => setSilinecek(o)} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ul className={s.mList}>
              {ogeler.map((o, i) => {
                const anahtar = `m-${o.code}-${o.time}`;
                const acik = acikSatir === anahtar;
                const d = SIPARIS_DURUM[o.state] || SIPARIS_DURUM.processing;
                const gun = gunuAl(o.time);
                const yeniGun = params.sort === 'time' && gun && gun !== gunuAl(i > 0 ? ogeler[i - 1].time : '');
                const go = gunOzet[gun] || { n: 0, tutar: 0 };
                return (
                  <li key={anahtar} className={s.mItem}>
                    {yeniGun && (
                      <div className={s.mDay}>
                        <span className={s.dayLabel}>{gunBasligi(gun)}</span>
                        <span className={s.dayCount}>{formatNumber(go.n)} · {formatTRYShort(go.tutar)}</span>
                      </div>
                    )}
                    <button type="button" className={s.mHead} onClick={() => setAcikSatir(acik ? null : anahtar)}>
                      <span className={s.mMain}>
                        <span className={s.mName}>{o.title || o.code}</span>
                        <span className={s.sub}>{o.code} · {saatTR(o.time)}</span>
                      </span>
                      <span className={s.mRight}>
                        <span className={s.mAmount}>{formatTRY(o.amount)}</span>
                        <span className={`${s.status} ${s[d.cls]}`}>{d.label}</span>
                      </span>
                      {acik ? <ChevronUp size={16} className={s.mChev} /> : <ChevronDown size={16} className={s.mChev} />}
                    </button>
                    <div className={s.mMeta}>
                      <span>{formatNumber(o.lines)} kalem</span>
                      <span>{formatNumber(o.qty)} adet</span>
                      <span>{DEPO_ETIKET[o.warehouse] || o.warehouse}</span>
                      {o.erp_no && <span className={s.mono}>{o.erp_no}</span>}
                    </div>
                    {acik && (
                      <div className={s.mExp}>
                        {o.error_message && (
                          <div className={s.errNote}><AlertTriangle size={15} /> {o.error_message}</div>
                        )}
                        <Kalemler nolar={o.order_nos} />
                        <SilButonu onClick={() => setSilinecek(o)} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className={s.pager}>
              <span className={s.pagerInfo}>
                {formatNumber(ilkSira + 1)}–{formatNumber(ilkSira + ogeler.length)} / {formatNumber(toplam)}
              </span>
              <label className={s.perBox}>
                <span className={s.fieldLabel}>Sayfa başına</span>
                <select className={s.selectSm} value={params.per}
                  onChange={(e) => guncelle({ per: e.target.value })}>
                  {((data && data.page_sizes) || [20, 50, 100]).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <div className={s.pagerBtns}>
                <button type="button" className={s.pageBtn} disabled={sayfa <= 1 || loading}
                  onClick={() => guncelle({ page: String(sayfa - 1) }, false)} aria-label="Önceki">
                  <ChevronLeft size={16} />
                </button>
                <span className={s.pageNo}>{formatNumber(sayfa)} / {formatNumber(sonSayfa)}</span>
                <button type="button" className={s.pageBtn} disabled={sayfa >= sonSayfa || loading}
                  onClick={() => guncelle({ page: String(sayfa + 1) }, false)} aria-label="Sonraki">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <ToastStack items={toast.items} onDismiss={toast.dismiss} />
      <OrderDeleteModal siparis={silinecek} onKapat={silKapat} onSilindi={silindi} />
    </div>
  );
}
